import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  CharacterSpell,
  StoredSpell,
  Potence,
  CreatureGift,
  CreatureLimitation,
  CreatureInstanceGift,
} from "@dndModels";

import API from "@utilities/smart_api";
import DataUtilities from '@dndUtilities/data_utilities';

import DandDButtonBox from "@dndComponents/DandDButtonBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import LoadingBox from '@components/input/LoadingBox';
import DandDCharacterUtilities from '@dndUtilities/char_utilities';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import { DisablableOption } from '@models';

interface Props {
  char: Character;
  spell?: CharacterAbility | CharacterSpell | StoredSpell;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  shared: boolean;
  potence: Potence;
  onChange: (change_types: string[]) => void; 
}

const api = API.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterEnhanceCreatureEffect({
  char,
  spell,
  processing,
  disabled,
  shared,
  potence,
  onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [is_processing, setProcessing] = useState(false);
  const [selected_id, setSelectedId] = useState("");
  const [selected_gift_id, setSelectedGiftId] = useState("");
  const [creature_gifts, setCreatureGifts] = useState<CreatureGift[]>([]);
  const [minion_options, setMinionOptions] = useState<DisablableOption[]>([]);

  const load = useCallback( async () => {
    setCreatureGifts(await data_util.getCreatureGifts());
    // debugger;
    if (potence.limits instanceof CreatureLimitation) {
      const creature_limits = potence.limits;
      const valid_creatures = char.minions.filter(o => char_util.fitCreatureLimit({ ci: o, limit: creature_limits }));
      setMinionOptions(valid_creatures.map(o => {
        const opt: DisablableOption = {
          id: o.true_id,
          text: o.name
        };
        return opt;
      }));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id)));

  const renderCreatureSelect = () => {
    return (
      <Grid size={6}>
        <SelectStringBox
          name="Creature to Enhance"
          options={minion_options}
          value={selected_id}
          onChange={setSelectedId}
        />
      </Grid>
    );
  }

  const renderButtons = () => {
    let use_gift_id = selected_gift_id;
    if (potence.enhance_ids.length === 1) {
      use_gift_id = potence.enhance_ids[0];
    }
    const gift = creature_gifts.find(o => o._id === use_gift_id);
    const selected_minion = char.minions.find(o => o.true_id === selected_id);
    return (
      <>
        <Grid size={3}>
          <DandDButtonBox
            name={ gift === undefined ? "Apply" : (selected_minion && selected_minion.cgift_ids.includes(gift._id) ? "Remove" : `Apply`) }
            disabled={ !enabled || gift === undefined || selected_id === "" }
            onClick={async () => {
              const minion = char.minions.find(o => o.true_id === selected_id);
              if (gift && minion) {
                await api.loadObject(gift);
                minion.spirit.cgift_ids.push(gift._id);
                const creature_gift = new CreatureInstanceGift();
                creature_gift.copy_source({ char: minion, feature_source: gift });
                minion.spirit.creature_gifts.push(creature_gift);
                char.subscription_holder.recalculate();
                char.changed();
              }
            }} 
          />
        </Grid>
        <Grid size={3}>
          <DandDButtonBox
            name={ `Remove All` }
            disabled={ !enabled || gift === undefined || char.minions.filter(o => 
                o.cgift_ids.includes(gift._id)).length === 0 }
            onClick={() => {
              // debugger;
              if (gift) {
                for (const minion of char.minions.filter(o => 
                  o.cgift_ids.includes(gift._id))) {
                  // if (minion instanceof ComplexCreatureInstance) {
                  //   minion.creature_gift_cfss = 
                  //     minion.creature_gift_cfss.filter(o => o.source_id !== gift._id);
                  // }
                }
                onChange(["Creatures"]);
              }
            }} 
          />
        </Grid>
      </>
    );
  }

  // Select a valid item
  // Apply the mik
  // Also have a button to clear it from all items.
  // debugger;
  if (!fetched || loading) {
    return <LoadingBox />;
  }
  if (potence.enhance_ids.length === 1) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={4}>

        </Grid>
        <Grid size={8}
          container spacing={0} direction="row">
          { renderCreatureSelect() }
          { renderButtons() }
        </Grid>
      </Grid>
    );
  } else {
    // console.log(potence);
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={4}>
          <SelectObjectBox
            name="Select Option"
            data_type="creature_gift"
            value={selected_gift_id}
            filter={{ "_id": { "in": potence.enhance_ids }}}
            onChange={(changed: string) => {
              setSelectedGiftId(changed);
              setReloading(true);
            }}
          />
        </Grid>
        <Grid size={8}
          container spacing={0} direction="row">
          { renderCreatureSelect() }
          { renderButtons() }
        </Grid>
      </Grid>
    );
  }
}
